import { HubConnection, HubConnectionBuilder, IHttpConnectionOptions } from "@microsoft/signalr";
import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { $$, IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PermissionCacheService } from "@seriously-rad/seriouslyrad-framework-core/PermissionCacheService";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { IRadObject, Property, PropertyBoolean, RadObject, RadObjectPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ApplicationInformationService } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { WebApiClient } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClient";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { ApplicationDataService } from "@seriously-rad/seriouslyrad-framework-core/Ui/ApplicationDataService";
import { NotificationHubService } from "./NotificationHubService";
import { NAMESPACE } from "./_Namespace";

export type NotificationHubPropertyValues =
    RadObjectPropertyValues
    & {
        authenticated: boolean;
        connected: boolean;
        connectionStarted: boolean;
        engineAvailable: boolean;
    };

export type NotificationHubProperties = {
    [name in keyof NotificationHubPropertyValues]: Property<NotificationHubPropertyValues[name]>
};

export interface INotificationHub extends IRadObject, NotificationHubProperties
{
    registerMethod(name: string, fn: Function): void;
}

export class NotificationHub extends RadObject implements INotificationHub
{
    // #region Static

    public static override classNamespace = NAMESPACE + "NotificationHub";
    public static override className = "NotificationHub";

    // #region Constructor

    static {
        $$(document).on("SeriouslyRAD.NotificationHub:RegisterMethods", (evt: IJNativeEvent, hub: NotificationHub) =>
        {
            hub.registerMethod("permissionsChanged", () =>
            {
                ServiceManager.getOptional(PermissionCacheService)?.refresh();
            });

            hub.registerMethod("userSettingsChanged", () =>
            {
                $$(document).trigger("SeriouslyRAD.NotificationHub:UserSettingsChanged");
            });
        });

        $$(window).on("online", () =>
        {
            ServiceManager.getOptional(NotificationHubService)?.connected.set(true);
        });

        $$(window).on("offline", () =>
        {
            ServiceManager.getOptional(NotificationHubService)?.connected.set(false);
        });
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected connection?: HubConnection;
    protected methods?: { [name: string]: Function };
    protected connectionStartedTimeoutId?: number;

    // #endregion

    // #region Properties

    public authenticated!: PropertyBoolean;
    public connected!: PropertyBoolean;
    public connectionStarted!: PropertyBoolean;
    public engineAvailable!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<NotificationHubPropertyValues>)
    {
        super(propertyValues);

        this._bindEvents();
        this.methods = {};

        $$(document).trigger("SeriouslyRAD.NotificationHub:RegisterMethods", this);
    }

    // #endregion

    // #region Public Methods

    public registerMethod(name: string, fn: Function): void
    {
        Assert.isNotNullOrUndefined(this.methods, "this.methods");

        this.methods[name] = fn;
    }

    // #endregion

    // #region Protected Methods

    protected _addMethodToConnection(methodName: string, fn: Function)
    {
        this.connection?.on(methodName, (...args) =>
        {
            try { fn.apply(this, args); }
            catch (e) { console.error(e); throw e; }
        });
    }
    protected _authenticatedChanged(change: PropertyChange<boolean>)
    {
        this._updateConnection();
    }
    protected _bindEvents()
    {
        $$(document).on("SeriouslyRAD.Application:Authenticated", { self: this }, this.onAuthenticated);
        $$(document).on("SeriouslyRAD.Application:Connected", { self: this }, this.onConnected);
        $$(document).on("SeriouslyRAD.Application:Disconnected", { self: this }, this.onDisconnected);
        $$(document).on("SeriouslyRAD.Application:Unauthenticated", { self: this }, this.onUnauthenticated);

        $$(document).on("SeriouslyRAD.NotificationHub:Connected", { self: this }, this.onNotificationHubConnected);
    }
    protected _connectedChanged(change: PropertyChange<boolean>)
    {
        this._updateConnection();
    }
    protected _connectionStartedChanged(change: PropertyChange<boolean>)
    {
        if (this.connectionStartedTimeoutId)
            window.clearTimeout(this.connectionStartedTimeoutId);

        this.connectionStartedTimeoutId =
            window.setTimeout(
                () =>
                {
                    if (change.currentValue)
                        this._ensureConnectionStartedAsync();
                    else
                        this._ensureConnectionStoppedAsync();
                }
            );
    }
    protected _createNewConnection()
    {
        let
            accessTokenFactory;

        const
            authenticationData = ServiceManager.getOptional(ApplicationDataService)?.authenticationData.get();

        if (authenticationData)
        {
            switch (authenticationData.scheme)
            {
                case "Bearer":
                    {
                        accessTokenFactory = () =>
                        {
                            return (
                                ServiceManager.getRequired(WebApiClientService)
                                    .createApiMethodRequest(
                                        "DataService",
                                        "CallDataService",
                                        {
                                            id: "cc235279-29c2-4a76-b08a-71e574e614ee",
                                            operation: "GetSignalRAccessToken"
                                        },
                                        null,
                                        false
                                    )
                                    .executeAsync()
                                    .then(response =>
                                    {
                                        return response ? response.accessToken : null;
                                    })
                            );
                        };
                    }
            }
        }

        this.connection = new HubConnectionBuilder()
            .withUrl(StringHelper.format("{0}signalr/hubs", ServiceManager.getRequired(ApplicationInformationService).serverVirtualDirectory), {
                accessTokenFactory: accessTokenFactory
            } as IHttpConnectionOptions)
            .withAutomaticReconnect()
            .build();

        this.connection.onreconnected(connectionId =>
        {
            $$(document).trigger("SeriouslyRAD.NotificationHub:Connected", this);
        });

        for (let methodName in this.methods)
            this._addMethodToConnection(methodName, this.methods[methodName]);
    }
    protected _engineAvailableChanged(change: PropertyChange<boolean>)
    {
        this._updateConnection();
    }
    protected async _ensureConnectionStartedAsync()
    {
        await this._ensureConnectionStoppedAsync();

        this._createNewConnection();
        await this.connection?.start();

        $$(document).trigger("SeriouslyRAD.NotificationHub:Connected", this);
    }
    protected async _ensureConnectionStoppedAsync()
    {
        if (!this.connection)
            return;

        this.connection.onreconnected(() => { });
        await this.connection.stop();
        delete this.connection;
    }
    protected _updateConnection()
    {
        const
            authenticated = this.authenticated.get(),
            connected = this.connected.get(),
            engineAvailable = this.engineAvailable.get();

        this.connectionStarted.set(authenticated && connected && engineAvailable);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: NotificationHubPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.authenticated = new PropertyBoolean(this, propertyValues.authenticated, this._authenticatedChanged);
        this.connected = new PropertyBoolean(this, propertyValues.connected, this._connectedChanged);
        this.connectionStarted = new PropertyBoolean(this, propertyValues.connectionStarted, this._connectionStartedChanged);
        this.engineAvailable = new PropertyBoolean(this, propertyValues.engineAvailable, this._engineAvailableChanged);
    }

    // #endregion

    // #region Event Handlers

    protected onAuthenticated(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this,
            engines = ServiceManager.getOptional(PermissionCacheService)?.engines.get();

        self.authenticated.set(true);
        self.engineAvailable.set(!!(engines?.notificationEngine));
    }
    protected onConnected(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.connected.set(true);
    }
    protected onDisconnected(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.connected.set(false);
    }
    protected onNotificationHubConnected(evt: IJNativeEvent, notificationHub: NotificationHub)
    {
        Assert.isNotNullOrUndefined(notificationHub.connection, "notificationHub.connection");
        WebApiClient.addStaticHeader("X-SignalRHub-ConnectionId", notificationHub.connection.connectionId);
    }
    protected onUnauthenticated(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.authenticated.set(false);
    }

    // #endregion

    // #endregion
}
